import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {IDatePropertyTypeInterfaces} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/DatePropertyTypeStrategy/types/IDatePropertyTypeInterfaces'
import {expandDateSearchVariants} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/DatePropertyTypeStrategy/expandDateSearchVariants'
import {
  DefaultDateFormat,
  dateValueToText,
  toDateValue,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/DatePropertyTypeStrategy/dateValue'
import {format} from 'date-fns'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {IFilterItem} from '@toolify/server/src/services/FilterService/types/IFilterItem'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {DateGroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/DatePropertyTypeStrategy/enum/DateGroupGranularityType'
import {GroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/types/GroupGranularityType'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {COMMON_GROUP_SORT_LABEL_KEYS} from '@toolify/client/src/modules/databases/utils/groupSortModes'
import {dateGroupGranularityDescriptor} from '@toolify/client/src/modules/databases/utils/dateGroupGranularityDescriptor'
import {
  getRelativeDateLabelFromParts,
  isRelativeDateFormat,
} from '@toolify/client/src/modules/databases/utils/getRelativeDateLabel'

export function useDateValueTypeStrategy() {
  const strategyComponentType = PropertyStrategyComponentType.Dropdown

  const fullValueToText = (value: IDatePropertyTypeInterfaces['fullValue'], _property: IDatabasePropertyResponseInstance) => {
    return dateValueToText(value)
  }

  const getDisplayValue = (
    value: unknown,
    propertyOptions: {format?: string, dateFormat?: string} | null | undefined,
  ): unknown => {
    const dateValue = toDateValue(value)
    if(!dateValue) {
      return value
    }
    const dateFormat = propertyOptions?.dateFormat || propertyOptions?.format || DefaultDateFormat
    const formatPart = (part: {date: string, time: string}): string => {
      if(!part.date) {
        return ''
      }
      if(isRelativeDateFormat(dateFormat)) {
        return getRelativeDateLabelFromParts(part.date, part.time) || part.date
      }
      const [year, month, day] = part.date.split('-').map(Number)
      const [hours = 0, minutes = 0] = part.time ? part.time.split(':').map(Number) : []
      const parsedDate = new Date(year, month - 1, day, hours, minutes)
      if(isNaN(parsedDate.getTime())) {
        return part.date
      }
      try {
        return format(parsedDate, part.time ? `${dateFormat} HH:mm` : dateFormat)
      } catch {
        return part.date
      }
    }
    const from = formatPart(dateValue.from)
    const to = formatPart(dateValue.to)
    return to ? `${from} - ${to}` : from
  }

  const sortGroupItems = (a: {fullValue: IDatePropertyTypeInterfaces['fullValue']}, b: {fullValue: IDatePropertyTypeInterfaces['fullValue']}): number => {
    const aFullValue = a.fullValue
    const bFullValue = b.fullValue

    const aHasDate = Boolean(aFullValue?.from?.date)
    const bHasDate = Boolean(bFullValue?.from?.date)

    if (!aHasDate && !bHasDate) return 0
    if (!aHasDate) return 1
    if (!bHasDate) return -1

    const aDate = aFullValue.from.date
    const bDate = bFullValue.from.date

    if (aDate !== bDate) {
      return aDate.localeCompare(bDate)
    }

    const aTime = aFullValue.from?.time || ''
    const bTime = bFullValue.from?.time || ''

    return aTime.localeCompare(bTime)
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[]): IFilter => {
    const filterItems: IFilterItem[] = []
    const subProperties = ['from.date', 'to.date', 'from.time', 'to.time']
    const searchVariants = expandDateSearchVariants(search)
    for(const databaseProperty of databaseProperties) {
      for(const subProperty of subProperties) {
        for(const variant of searchVariants) {
          filterItems.push({
            type: FilterItemType.Property,
            id: databaseProperty.id,
            subProperty,
            operator: {
              type: FilterPropertyOperator.Contains,
            },
            payload: {
              type: FilterPropertyPayloadType.Static,
              value: variant,
            },
            isDisabled: false,
          })
        }
      }
    }
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: filterItems.flat(),
    }
  }

  const compareItemValues = (
    valueA: IDatePropertyTypeInterfaces['fullValue'],
    valueB: IDatePropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
  ): number => {
    if (!valueA && !valueB) return 0
    if (!valueA) return 1
    if (!valueB) return -1
    
    const dateA = valueA.from?.date || ''
    const dateB = valueB.from?.date || ''
    
    let comparison: number
    if (dateA !== dateB) {
      comparison = dateA.localeCompare(dateB)
    } else {
      const timeA = valueA.from?.time || ''
      const timeB = valueB.from?.time || ''
      comparison = timeA.localeCompare(timeB)
    }
    
    return sortDirection === '1' ? comparison : -comparison
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table || viewType === DatabaseViewType.Kanban

  const groupGranularity = dateGroupGranularityDescriptor

  const groupSort = {
    availableModes: [GroupSortModeType.ValueAsc, GroupSortModeType.ValueDesc, GroupSortModeType.CountDesc, GroupSortModeType.CountAsc],
    defaultMode: GroupSortModeType.ValueAsc,
    getLabelKey: (mode: GroupSortModeType): string => {
      if(mode === GroupSortModeType.ValueAsc) return 'database.header.groupSort.dateAsc'
      if(mode === GroupSortModeType.ValueDesc) return 'database.header.groupSort.dateDesc'
      return COMMON_GROUP_SORT_LABEL_KEYS[mode]
    },
  }

  const canMoveItemToAnotherGroup = (property: IDatabasePropertyResponseInstance | null | undefined, granularity?: GroupGranularityType | null): boolean => {
    if(property?.canBeModified === false) {
      return false
    }
    return !(!!granularity && granularity !== DateGroupGranularityType.Day)
  }

  return {
    fullValueToText,
    getDisplayValue,
    strategyComponentType,
    sortGroupItems,
    searchToFilterQuery,
    compareItemValues,
    canGroupByView,
    canMoveItemToAnotherGroup,
    groupGranularity,
    groupSort,
  }
}
